import React, {useState} from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { Button, TextField, Typography } from '@mui/material';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormHelperText from '@mui/material/FormHelperText';
import Switch from '@mui/material/Switch';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import { adminAddTheoryStage } from "../../api/Api";
import { getAvailableCategories, getAvailableSlugs } from '../Academy/Theory/stageRegistry';
import { useColors } from '../../theme/ColorModeContext';
import PageContainer from '../../shared/PageContainer';
import GlowCard from '../../shared/GlowCard';
import CyberpunkTitle from '../../shared/CyberpunkTitle';

interface PropsAdminTheoryStages {
    token: string;
}

const fieldSx = (colors: any) => ({
    '& .MuiOutlinedInput-root': {
        fontFamily: '"Share Tech Mono", monospace',
        fontSize: '0.9rem',
        color: colors.text.primary,
        '& fieldset': { borderColor: colors.border.default },
        '&:hover fieldset': { borderColor: colors.border.accent },
        '&.Mui-focused fieldset': { borderColor: colors.accent.cyan, boxShadow: `0 0 0 2px ${colors.accent.cyanGlow}` },
    },
    '& .MuiInputLabel-root': {
        fontFamily: '"Share Tech Mono", monospace',
        fontSize: '0.82rem',
        letterSpacing: '0.08em',
        color: colors.text.secondary,
        '&.Mui-focused': { color: colors.accent.cyan },
    },
});

const AdminAddTheoryStage = (props: PropsAdminTheoryStages) => {
    const colors = useColors();
    const navigate = useNavigate();
    const [processing, setProcessing] = useState(false);
    const [message, setMessage] = useState("");
    const categories = getAvailableCategories();
    const [category, setCategory] = useState(categories[0] ?? "");
    const slugOptions = getAvailableSlugs(category);
    const [slug, setSlug] = useState(slugOptions[0] ?? "");
    const [title, setTitle] = useState("");
    const [orderIndex, setOrderIndex] = useState(0);
    const [points, setPoints] = useState(1);
    const [active, setActive] = useState(false);

    const token = props.token;

    const handleCategoryChange = (newCategory: string) => {
        setCategory(newCategory);
        setSlug(getAvailableSlugs(newCategory)[0] ?? "");
    };

    const handleSubmit = async (e: any) => {
        e.preventDefault();
        setProcessing(true);
        const response = await adminAddTheoryStage(token, {
            category, slug, title, order_index: orderIndex, points, active
        });
        setProcessing(false);
        setMessage(response.msg);
        if (response.msg === "Theory stage created") {
            navigate("/admin/theory/stages");
        }
    };

    return (
        <PageContainer>
            <CyberpunkTitle
                segments={[
                    { label: 'Theory Stages', to: '/admin/theory/stages' },
                    { label: 'Add' },
                ]}
                mb="32px"
            />

            <GlowCard accentTop glowOnHover={false} sx={{ maxWidth: '600px' }}>
                <Box sx={{ padding: '32px' }}>
                    <Box component="form" onSubmit={handleSubmit} noValidate>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                            <FormControl sx={fieldSx(colors)} required fullWidth>
                                <InputLabel id="add-theory-stage-category-label">Category</InputLabel>
                                <Select
                                    labelId="add-theory-stage-category-label"
                                    label="Category"
                                    value={category}
                                    onChange={(e) => handleCategoryChange(e.target.value as string)}
                                    autoFocus
                                >
                                    {categories.map((c) => (
                                        <MenuItem key={c} value={c}>{c}</MenuItem>
                                    ))}
                                </Select>
                                <FormHelperText>Skill-tree category this stage belongs to</FormHelperText>
                            </FormControl>
                            <FormControl sx={fieldSx(colors)} required fullWidth disabled={slugOptions.length === 0}>
                                <InputLabel id="add-theory-stage-slug-label">Slug</InputLabel>
                                <Select
                                    labelId="add-theory-stage-slug-label"
                                    label="Slug"
                                    value={slug}
                                    onChange={(e) => setSlug(e.target.value as string)}
                                >
                                    {slugOptions.map((s) => (
                                        <MenuItem key={s} value={s}>{s}</MenuItem>
                                    ))}
                                </Select>
                                <FormHelperText>
                                    {slugOptions.length === 0
                                        ? 'No hardcoded content registered for this category in stageRegistry.ts'
                                        : 'Only slugs with reading content already in stageRegistry.ts are selectable'}
                                </FormHelperText>
                            </FormControl>
                            <TextField
                                sx={fieldSx(colors)}
                                required
                                fullWidth
                                label="Title"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                            />
                            <Box sx={{ display: 'flex', gap: '16px' }}>
                                <TextField
                                    sx={{ ...fieldSx(colors), flex: 1 }}
                                    required
                                    label="Order"
                                    type="number"
                                    value={orderIndex}
                                    onChange={(e) => setOrderIndex(Number(e.target.value))}
                                />
                                <TextField
                                    sx={{ ...fieldSx(colors), flex: 1 }}
                                    required
                                    label="Points"
                                    type="number"
                                    value={points}
                                    onChange={(e) => setPoints(Number(e.target.value))}
                                />
                            </Box>

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={active}
                                        onChange={(e) => setActive(e.target.checked)}
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': { color: colors.accent.cyan },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: colors.accent.cyanDim },
                                        }}
                                    />
                                }
                                label={
                                    <Typography sx={{ fontFamily: '"Share Tech Mono", monospace', fontSize: '0.82rem', letterSpacing: '0.08em', color: colors.text.secondary }}>
                                        Active
                                    </Typography>
                                }
                            />

                            <Button
                                type="submit"
                                fullWidth
                                variant="outlined"
                                disabled={processing || !slug}
                                startIcon={processing ? <CircularProgress size={16} sx={{ color: colors.accent.cyan }} /> : <CheckCircleOutlineIcon />}
                                sx={{
                                    marginTop: '8px',
                                    borderColor: colors.accent.cyan,
                                    color: colors.accent.cyan,
                                    fontFamily: '"Share Tech Mono", monospace',
                                    fontWeight: 700,
                                    fontSize: '0.8rem',
                                    letterSpacing: '0.14em',
                                    textTransform: 'uppercase',
                                    padding: '12px',
                                    borderRadius: '6px',
                                    '&:hover': {
                                        backgroundColor: colors.accent.cyanGlow,
                                        borderColor: colors.accent.cyan,
                                        boxShadow: `0 0 20px ${colors.accent.cyan}33`,
                                    },
                                    '&:disabled': {
                                        borderColor: colors.border.default,
                                        color: colors.text.muted,
                                    },
                                }}
                            >
                                {processing ? 'Adding...' : 'Add Stage'}
                            </Button>

                            {message && (
                                <Box sx={{
                                    padding: '12px 16px',
                                    borderRadius: '6px',
                                    backgroundColor: colors.accent.cyanGlow,
                                    border: `1px solid ${colors.accent.cyan}44`,
                                    fontFamily: '"Share Tech Mono", monospace',
                                    fontSize: '0.8rem',
                                    color: colors.accent.cyan,
                                    letterSpacing: '0.06em',
                                }}>
                                    {message}
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Box>
            </GlowCard>
        </PageContainer>
    );
};

export default AdminAddTheoryStage;
